import { useQuery } from "@tanstack/react-query";
import { getAllDataSources } from "@/api/services/dataSourceService";
import { useApp } from "@/contexts/AppContext";
import { useDataSource } from "@/contexts/DataSourceContext";
import {
  getCashSourceLastSync,
  mostRecentSync,
} from "@/services/metricsService";

/**
 * Last successful sync timestamp of the data source behind the cash position.
 *
 * Live mode: the primary category data source from GET /api/v1/data-sources,
 * falling back to the most recently synced source.
 * Demo mode: the highest-priority connected cash source.
 *
 * @returns ISO timestamp, or null when nothing has synced
 */
export function useCashSourceLastSync(): string | null {
  const { mode } = useApp();
  const { dataSources } = useDataSource();
  const isLive = mode === "live";

  const { data } = useQuery({
    queryKey: ["dataSourcesLastSync"],
    queryFn: getAllDataSources,
    enabled: isLive,
    staleTime: 5 * 60 * 1000,
  });

  if (!isLive) {
    return getCashSourceLastSync(dataSources);
  }

  const sources = data?.data ?? [];
  const primary = sources.find((s) => s.isPrimaryCategorySource);
  if (primary?.lastSyncDateTime) return primary.lastSyncDateTime;

  return mostRecentSync(sources.map((s) => s.lastSyncDateTime));
}
